import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Sprout } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";

const EXTERNAL_LOGIN = "https://app.geteasystatement.com/login";

export const Header = () => {
  const { lang, toggle, t } = useLang();
  const [open, setOpen] = useState(false);

  const links = [
    { href: "/#features", label: t.nav.features },
    { href: "/#how", label: t.nav.howItWorks },
    { href: "/#pricing", label: t.nav.pricing },
    { href: "/#faq", label: t.nav.faq },
    { href: "/#contact", label: t.nav.contact },
  ];

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-stone-50/85 backdrop-blur-xl border-b border-stone-200/70"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link
          to="/"
          data-testid="header-logo"
          aria-label="Homepage"
          className="flex items-center gap-2 group"
        >
          <span className="w-9 h-9 rounded-lg bg-green-900 grid place-items-center text-stone-50 group-hover:bg-green-800 transition-colors">
            <Sprout size={18} strokeWidth={2.5} />
          </span>
          <span className="font-display text-xl text-stone-900 leading-none">
            Get Easy <span className="text-green-900">Statement</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.href.replace("#", "")}`}
              className="text-sm font-medium text-stone-600 hover:text-stone-900 transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="lang-toggle"
            aria-label="Toggle language"
            onClick={toggle}
            className="hidden sm:flex items-center gap-1 px-3 py-1.5 rounded-full border border-stone-300 text-xs font-semibold text-stone-700 hover:bg-stone-100 transition"
          >
            <span className={lang === "en" ? "text-green-900" : "text-stone-600"}>
              EN
            </span>
            <span className="text-stone-300">/</span>
            <span className={lang === "hi" ? "text-green-900" : "text-stone-600"}>
              हि
            </span>
          </button>

          <Link
            to="/download"
            className="hidden sm:inline-flex px-5 py-2 rounded-md border border-green-900 text-green-900 text-sm font-semibold hover:bg-green-50 transition-colors"
          >
            Download App
          </Link>

          <a
            href={EXTERNAL_LOGIN}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-login-btn"
            className="hidden sm:inline-flex px-5 py-2 rounded-md bg-green-900 text-stone-50 text-sm font-semibold hover:bg-green-800 transition-colors"
          >
            {t.nav.login}
          </a>

          <button
            data-testid="mobile-menu-toggle"
            aria-label="Toggle mobile menu"
            onClick={() => setOpen((o) => !o)}
            className="md:hidden p-2 text-stone-700"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden border-t border-stone-200 bg-stone-50"
        >
          <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="py-2 text-stone-700 font-medium"
              >
                {l.label}
              </a>
            ))}
            <button
              data-testid="mobile-lang-toggle"
              aria-label="Toggle language mobile"
              onClick={toggle}
              className="text-left py-2 font-semibold text-stone-700"
            >
              {lang === "en" ? "हिंदी" : "English"}
            </button>
            <Link
              to="/download"
              onClick={() => setOpen(false)}
              className="mt-2 px-5 py-3 rounded-md border border-green-900 text-green-900 text-center font-semibold"
            >
              Download App
            </Link>
            <a
              href={EXTERNAL_LOGIN}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-login-btn"
              className="mt-2 px-5 py-3 rounded-md bg-green-900 text-stone-50 text-center font-semibold"
            >
              {t.nav.login}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
